import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError, getErrorMessage } from '../api/client';
import { getStores } from '../api/stores';
import { approveVoidRequest, getVoidRequests, rejectVoidRequest } from '../api/voidRequests';
import { useAuth } from '../auth/AuthProvider';
import { canReviewVoidRequests, getStoreScopeOptions } from '../auth/permissions';
import { DateRangeField } from '../components/DateRangeField';
import { Modal } from '../components/Modal';
import { ShiftCloseDetailModal } from '../components/ShiftCloseHistoryPanel';
import { FilterBar, FilterQuickSelect, type FilterChip } from '../components/filters/FilterBar';
import { PageHeader } from '../components/ui/PageHeader';
import { RowToggle } from '../components/ui/RowToggle';
import { Field, LoadingPanel, Select } from '../components/ui/primitives';
import type { Store } from '../types/stores';
import {
  VOID_REQUEST_STATUS_LABELS,
  VOID_REQUEST_TARGET_TYPE_LABELS,
  type VoidRequestItem,
  type VoidRequestListFilters,
  type VoidRequestStatus,
  type VoidRequestTargetType,
} from '../types/voidRequests';
import { describePeriod, deviceToday } from '../utils/periods';
import { DEFAULT_STORE_TIMEZONE, resolveStoreTimezone, storeWallTimeToIso } from '../utils/timezones';

const PAGE_SIZE = 25;
// Sección destino del deep-link "Ver registro" por tipo (cortes van en sitio, no por hash).
const TARGET_SECTION: Record<string, string> = { PURCHASE: 'purchases', OTHER_EXPENSE: 'expenses', OTHER_SALE: 'other-sales' };
const STATUS_OPTIONS = [{ value: '', label: 'Todos' }, ...(Object.entries(VOID_REQUEST_STATUS_LABELS) as Array<[VoidRequestStatus, string]>).map(([value, label]) => ({ value, label }))];
const TARGET_TYPE_OPTIONS = [{ value: '', label: 'Todos' }, ...(Object.entries(VOID_REQUEST_TARGET_TYPE_LABELS) as Array<[VoidRequestTargetType, string]>).map(([value, label]) => ({ value, label }))];

/**
 * P-075 · El periodo se elige en días ('YYYY-MM-DD', como en el resto de la app), pero este endpoint
 * compara `requestedAt` contra `new Date(valor)` (voidRequestService.list): un día suelto se leería como
 * la medianoche UTC —las 18:00 del día anterior en el centro de México— y «hasta hoy» dejaría fuera casi
 * todo hoy. Por eso viaja el INSTANTE del principio y del final de cada día en la zona de la tienda. Solo
 * se traduce al pedir: lo aplicado sigue guardando el día, que es lo que muestran el selector y el chip.
 */
function voidRequestDateBounds(dateFrom: string | undefined, dateTo: string | undefined, timeZone: string): Pick<VoidRequestListFilters, 'dateFrom' | 'dateTo'> {
  return {
    dateFrom: dateFrom ? storeWallTimeToIso(`${dateFrom}T00:00:00`, timeZone) : undefined,
    // El último milisegundo del día: el backend compara con «menor o igual».
    dateTo: dateTo ? new Date(Date.parse(storeWallTimeToIso(`${dateTo}T23:59:59`, timeZone)) + 999).toISOString() : undefined,
  };
}

/**
 * La zona con la que se leen los días del periodo. Con tienda elegida, la suya. Sin tienda (el dueño arranca en
 * «Todas»), la de sus tiendas si todas comparten una: un negocio cuya única tienda está en Tijuana no debe leer
 * sus días con la hora del centro (Codex R2 · M3). Con tiendas en zonas distintas no hay un día único: se usa la
 * zona del negocio por defecto, como el resto de la pantalla.
 */
function periodTimeZone(stores: Store[], storeId: string | undefined): string {
  if (storeId) return resolveStoreTimezone(stores, { storeId });
  const zones = [...new Set(stores.map((store) => store.timezone).filter(Boolean))];
  return zones.length === 1 ? zones[0]! : DEFAULT_STORE_TIMEZONE;
}

/**
 * Fase 21A: un EMPLOYEE ve aquí solo SUS solicitudes (el backend las fuerza así sin importar los
 * filtros que se manden — ver voidRequestService.list). Un MANAGER/OWNER ve la cola de solicitudes
 * de sus tiendas/negocio y puede aprobar (aplica la anulación real del módulo correspondiente) o
 * rechazar. Nunca se anula nada desde aquí directamente sin pasar por la aprobación explícita.
 */
export function VoidRequestsPage() {
  const { user } = useAuth();
  const canReview = canReviewVoidRequests(user?.role);
  // Un EMPLOYEE no elige tienda: el backend le devuelve las SUYAS de todas sus tiendas e ignora `storeId`.
  const isEmployee = user?.role === 'EMPLOYEE';
  const [stores, setStores] = useState<Store[]>([]);
  // `load` es estable (useCallback sin dependencias) y la primera carga ocurre antes de que `stores`
  // llegue al estado: la zona horaria de las fechas se lee de aquí.
  const storesRef = useRef<Store[]>([]);
  const [filters, setFilters] = useState<VoidRequestListFilters>({});
  const [draftFilters, setDraftFilters] = useState<VoidRequestListFilters>({});
  const [items, setItems] = useState<VoidRequestItem[]>([]);
  const [page, setPage] = useState({ total: 0, offset: 0, hasMore: false });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>();
  const [notice, setNotice] = useState<string>();
  const [approveTarget, setApproveTarget] = useState<VoidRequestItem>();
  // "Ver registro" de un CORTE: modal en sitio encima de Solicitudes (mismo patrón corte↔respuesta).
  const [openShiftCloseId, setOpenShiftCloseId] = useState<string>();
  const [rejectTarget, setRejectTarget] = useState<VoidRequestItem>();
  // Tarjetas móviles colapsables (mismo patrón que Compras/Gastos).
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const toggleExpanded = (id: string) => setExpandedIds((prev) => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; });

  const load = useCallback(async (nextFilters: VoidRequestListFilters, offset = 0) => {
    setIsLoading(true);
    setError(undefined);
    try {
      const timeZone = periodTimeZone(storesRef.current, nextFilters.storeId);
      const result = await getVoidRequests({ ...nextFilters, ...voidRequestDateBounds(nextFilters.dateFrom, nextFilters.dateTo, timeZone), limit: PAGE_SIZE, offset });
      setFilters(nextFilters);
      setItems(result.items);
      setPage({ total: result.total, offset: result.offset, hasMore: result.hasMore });
    } catch (loadError) {
      setError(getErrorMessage(loadError, 'No fue posible cargar las solicitudes.'));
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Cómo abre la pantalla. P-075: quien revisa abre en «Pendiente» —lo que cuenta el número del menú—, como
   * un filtro aplicado que se quita con su «×»; quien solo envía solicitudes sigue viendo todas las suyas.
   * La tienda es la que impone el rol (un gerente no tiene «Todas»: el backend le exige una).
   */
  const startFiltersFor = (storeList: Store[]): VoidRequestListFilters => {
    const { defaultStoreId } = getStoreScopeOptions(user?.role, storeList);
    return { ...(defaultStoreId ? { storeId: defaultStoreId } : {}), ...(canReview ? { status: 'PENDING' as const } : {}) };
  };

  useEffect(() => {
    void getStores().then((result) => {
      storesRef.current = result.stores;
      setStores(result.stores);
      const initialFilters = startFiltersFor(result.stores);
      setFilters(initialFilters);
      setDraftFilters(initialFilters);
      void load(initialFilters);
    }).catch((loadError) => setError(getErrorMessage(loadError, 'No fue posible cargar las tiendas.')));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refreshAfterChange = async (message: string) => {
    setNotice(message);
    await load(filters, page.offset);
  };

  const { allowsAllStores, defaultStoreId: forcedStoreId } = getStoreScopeOptions(user?.role, stores);
  const timeZone = (storeId: string) => stores.find((store) => store.id === storeId)?.timezone ?? DEFAULT_STORE_TIMEZONE;
  /**
   * P-075 (S4): el selector de tienda solo se dibujaba para el dueño, así que un gerente con dos o más
   * tiendas quedaba encerrado en la primera. Ahora lo ve quien revisa y tiene más de una tienda; «Todas las
   * tiendas» sigue siendo solo del dueño (al gerente el backend le exige una).
   */
  const showStoreFilter = !isEmployee && stores.length > 1;
  const storeOptions = [
    ...(allowsAllStores ? [{ value: '', label: 'Todas las tiendas' }] : []),
    ...stores.map((store) => ({ value: store.id, label: store.name })),
  ];
  const startFilters = startFiltersFor(stores);
  const storeChosenByUser = Boolean(filters.storeId) && filters.storeId !== forcedStoreId;
  const hasDateWindow = Boolean(filters.dateFrom || filters.dateTo);
  const isStartView = (filters.status ?? '') === (startFilters.status ?? '') && !filters.targetType && !storeChosenByUser && !hasDateWindow;

  /**
   * P-075 · La barra de filtros (mismo contrato que Ventas e ingresos, sin buscador: la API no busca). Lo
   * aplicado vive en `filters`; el periodo, los rápidos y cada «×» se aplican sobre lo aplicado; el panel
   * «Filtros» trabaja en borrador y se pide con «Ver resultados».
   */
  const applyPatch = (patch: Partial<VoidRequestListFilters>) => { if (!isLoading) void load({ ...filters, ...patch }, 0); };
  const panelCount = (storeChosenByUser ? 1 : 0) + (filters.status ? 1 : 0) + (filters.targetType ? 1 : 0);
  const chips: FilterChip[] = [
    ...(hasDateWindow ? [{ id: 'dates', label: `Fechas: ${describePeriod({ from: filters.dateFrom ?? '', to: filters.dateTo ?? '' }, deviceToday())}`, onRemove: () => applyPatch({ dateFrom: undefined, dateTo: undefined }) }] : []),
    ...(storeChosenByUser ? [{ id: 'store', label: `Tienda: ${stores.find((store) => store.id === filters.storeId)?.name ?? 'elegida'}`, onRemove: () => applyPatch({ storeId: forcedStoreId }) }] : []),
    ...(filters.status ? [{ id: 'status', label: `Estado: ${VOID_REQUEST_STATUS_LABELS[filters.status]}`, onRemove: () => applyPatch({ status: undefined }) }] : []),
    ...(filters.targetType ? [{ id: 'target', label: `Registro: ${VOID_REQUEST_TARGET_TYPE_LABELS[filters.targetType]}`, onRemove: () => applyPatch({ targetType: undefined }) }] : []),
  ];
  // La pantalla no tenía «Limpiar». Con chips, vuelve a como abre (el patrón de P-075) y solo aparece
  // cuando hay algo distinto de eso: en la vista de arranque no haría nada.
  const clearFilters = () => { if (!isLoading) { setDraftFilters(startFilters); void load(startFilters, 0); } };
  const panelFields = <>
    {showStoreFilter && <Field label="Tienda">
      <Select onChange={(event) => setDraftFilters({ ...draftFilters, storeId: event.target.value || undefined })} value={draftFilters.storeId || ''}>
        {storeOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
      </Select>
    </Field>}
    <Field label="Estado">
      <Select onChange={(event) => setDraftFilters({ ...draftFilters, status: (event.target.value || undefined) as VoidRequestStatus | undefined })} value={draftFilters.status || ''}>
        {STATUS_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
      </Select>
    </Field>
    <Field label="Tipo de registro">
      <Select onChange={(event) => setDraftFilters({ ...draftFilters, targetType: (event.target.value || undefined) as VoidRequestTargetType | undefined })} value={draftFilters.targetType || ''}>
        {TARGET_TYPE_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
      </Select>
    </Field>
  </>;

  return <div className="n3-screen admin-page">
    {/* P-075 (K7): la bandeja también trae cambios (MODIFY), no solo anulaciones. */}
    <PageHeader
      description={canReview
        ? 'Aprueba o rechaza lo que tu equipo pide anular o cambiar de una compra, un gasto, una venta, un corte o una merma.'
        : 'El estado de las anulaciones y los cambios que has pedido.'}
      title="Solicitudes de anulación y cambio"
    />

    {notice && <div className="alert alert--success">{notice}</div>}
    {error && <div className="alert alert--error">{error}</div>}

    <FilterBar
      busy={isLoading}
      chips={chips}
      label="Filtrar solicitudes"
      onClear={isStartView ? undefined : clearFilters}
      panel={{
        applying: isLoading,
        content: panelFields,
        count: panelCount,
        onApply: () => { if (!isLoading) void load({ ...draftFilters, dateFrom: filters.dateFrom, dateTo: filters.dateTo }, 0); },
        onOpen: () => setDraftFilters({ ...filters }),
        onReset: () => setDraftFilters({ ...startFilters }),
      }}
      period={<DateRangeField disabled={isLoading} from={filters.dateFrom || ''} onChange={(from, to) => applyPatch({ dateFrom: from || undefined, dateTo: to || undefined })} to={filters.dateTo || ''} />}
      quick={<>
        {showStoreFilter && <FilterQuickSelect disabled={isLoading} label="Tienda" onChange={(value) => applyPatch({ storeId: value || undefined })} options={storeOptions} value={filters.storeId || ''} />}
        <FilterQuickSelect disabled={isLoading} label="Estado" onChange={(value) => applyPatch({ status: (value || undefined) as VoidRequestStatus | undefined })} options={STATUS_OPTIONS} value={filters.status || ''} />
        <FilterQuickSelect disabled={isLoading} label="Tipo de registro" onChange={(value) => applyPatch({ targetType: (value || undefined) as VoidRequestTargetType | undefined })} options={TARGET_TYPE_OPTIONS} value={filters.targetType || ''} />
      </>}
      summary={`${page.total} ${page.total === 1 ? 'solicitud' : 'solicitudes'}`}
    />

    {isLoading ? <LoadingPanel label="Cargando solicitudes…" /> : items.length ? <>
      {/* Pre-client UX hardening (móvil): mismo patrón de tarjetas de Compras/Gastos/Cortes — nada
          de tabla de escritorio comprimida, "Ver registro" nunca se corta, y motivo/resolución
          siguen a un toque (y siempre se re-muestran en el modal de Aprobar antes de decidir). */}
      <div className="data-table-wrap">
        <table className="data-table data-table--cards n3-has-row-toggle">
          <thead><tr><th>Solicitud</th><th>Solicitante</th><th>Tienda</th><th>Detalle</th><th>Estado</th><th>Acciones</th></tr></thead>
          <tbody>{items.map((item) => <tr className={expandedIds.has(item.id) ? 'dt-card--open' : 'dt-card--closed'} onClick={() => toggleExpanded(item.id)} key={item.id}>
            <td className="dt-card-main">
              {/* P-075: el tipo se nombra como la pantalla («anulación y cambio»), no con un verbo suelto. */}
              <strong><span className={`status-pill ${item.type === 'MODIFY' ? 'status-pill--pending' : 'status-pill--inactive'}`}>{item.type === 'MODIFY' ? 'Cambio' : 'Anulación'}</span> {item.targetLabel || VOID_REQUEST_TARGET_TYPE_LABELS[item.targetType]}</strong>
              <small>{new Intl.DateTimeFormat('es-MX', { dateStyle: 'medium', timeStyle: 'short', timeZone: timeZone(item.storeId) }).format(new Date(item.requestedAt))}</small>
              <RowToggle expanded={expandedIds.has(item.id)} label={item.targetLabel || VOID_REQUEST_TARGET_TYPE_LABELS[item.targetType]} onToggle={() => toggleExpanded(item.id)} />
            </td>
            <td data-label="Solicitante">{item.requestedByName || 'Sin usuario'}</td>
            <td data-label="Tienda">{item.storeName}</td>
            <td className="void-request-cell" data-label="Detalle"><small className="void-request-cell__detail">{item.type === 'MODIFY' ? `Cambio pedido: ${item.requestedChange || item.reason}` : `Motivo: ${item.reason}`}</small></td>
            <td className="dt-card-keep" data-label="Estado"><VoidRequestStatusPill status={item.status} /></td>
            <td className="dt-card-actions dt-card-keep">
              {/* Ver el registro ANTES de decidir (Lote B): cortes en sitio (modal encima, sin perder
                  contexto); compras/gastos/ventas via deep-link a su detalle. */}
              <div className="table-actions">
                <button className="text-button" onClick={(event) => { event.stopPropagation(); if (item.targetType === 'SHIFT_CLOSE') setOpenShiftCloseId(item.targetId); else if (item.targetType === 'INVENTORY_WASTE') window.location.hash = 'waste'; else window.location.hash = `${TARGET_SECTION[item.targetType]}/${item.targetId}`; }} type="button">Ver registro</button>
                {item.status === 'PENDING' && canReview && <>
                  <button className="text-button" onClick={(event) => { event.stopPropagation(); setApproveTarget(item); }} type="button">Aprobar</button>
                  <button className="text-button text-button--danger" onClick={(event) => { event.stopPropagation(); setRejectTarget(item); }} type="button">Rechazar</button>
                </>}
              </div>
              {item.status !== 'PENDING' && item.reviewedByName && <small>{item.status === 'APPROVED' ? 'Aprobada' : 'Rechazada'} por {item.reviewedByName}{item.reviewNotes ? `: ${item.reviewNotes}` : ''}</small>}
            </td>
          </tr>)}</tbody>
        </table>
      </div>
      <div className="coverage-pagination">
        <span>Mostrando {page.offset + 1}–{page.offset + items.length} de {page.total}</span>
        <div>
          <button className="button button--ghost" disabled={page.offset === 0} onClick={() => void load(filters, Math.max(0, page.offset - PAGE_SIZE))} type="button">Anterior</button>
          <button className="button button--ghost" disabled={!page.hasMore} onClick={() => void load(filters, page.offset + PAGE_SIZE)} type="button">Siguiente</button>
        </div>
      </div>
    </> : <div className="empty-panel">{canReview && isStartView
      // La vista de arranque de quien revisa: «nada pendiente» es la buena noticia, y el resto está a un toque.
      ? <><strong>Nada pendiente</strong><span>No hay solicitudes de anulación o cambio por revisar.</span><div className="empty-panel__actions"><button className="text-button" onClick={() => applyPatch({ status: undefined })} type="button">Ver todas las solicitudes</button></div></>
      : chips.length > 0
        ? <><strong>Ninguna solicitud coincide</strong><span>Quita algún filtro para ver más.</span></>
        : <><strong>Sin solicitudes</strong><span>{canReview ? 'Todavía nadie ha pedido anular o cambiar un registro.' : 'No has pedido anular ni cambiar ningún registro todavía.'}</span></>}</div>}

    {approveTarget && <ApproveVoidRequestModal
      request={approveTarget}
      onClose={() => setApproveTarget(undefined)}
      onApproved={async (message) => { setApproveTarget(undefined); await refreshAfterChange(message); }}
      onAutoRejected={async (message) => {
        setApproveTarget(undefined);
        setNotice(undefined);
        // Primero se recarga (load limpia el error) y DESPUÉS se muestra por qué no se anuló.
        await load(filters, page.offset);
        setError(message);
      }}
    />}
    {openShiftCloseId && <ShiftCloseDetailModal shiftCloseId={openShiftCloseId} stores={stores} onClose={() => setOpenShiftCloseId(undefined)} onVoided={() => { setOpenShiftCloseId(undefined); void load(filters, page.offset); }} />}
    {rejectTarget && <RejectVoidRequestModal
      request={rejectTarget}
      onClose={() => setRejectTarget(undefined)}
      onRejected={async (message) => { setRejectTarget(undefined); await refreshAfterChange(message); }}
    />}
  </div>;
}

function VoidRequestStatusPill({ status }: { status: VoidRequestStatus }) {
  const tone = status === 'APPROVED' ? 'applied' : status === 'REJECTED' || status === 'CANCELLED' ? 'reverted' : 'pending';
  return <span className={`consumption-status consumption-status--${tone}`}>{VOID_REQUEST_STATUS_LABELS[status]}</span>;
}

function ApproveVoidRequestModal({ request, onClose, onApproved, onAutoRejected }: { request: VoidRequestItem; onClose: () => void; onApproved: (message: string) => Promise<void>; onAutoRejected: (message: string) => Promise<void> }) {
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string>();
  const isModify = request.type === 'MODIFY';
  // Modificar un CORTE: al aprobar, el backend ANULA el corte automáticamente (un corte inmutable
  // solo se "modifica" anulando + recapturando) — el modal debe decirlo con claridad.
  const isModifyShiftClose = isModify && request.targetType === 'SHIFT_CLOSE';

  const confirmApprove = async () => {
    if (isSaving) return;
    setIsSaving(true);
    setError(undefined);
    try {
      const result = await approveVoidRequest(request.id, { confirm: true });
      await onApproved(result.message);
    } catch (approveError) {
      // F2: el registro ya no se podía anular (p. ej. la merma se contó después) y la solicitud quedó
      // rechazada sola: se cierra y la lista se refresca con la explicación.
      if (approveError instanceof ApiError && (approveError.payload as { error?: string } | undefined)?.error === 'VOID_REQUEST_AUTO_REJECTED') {
        await onAutoRejected(getErrorMessage(approveError, 'No se anuló y la solicitud quedó rechazada.'));
        return;
      }
      setError(getErrorMessage(approveError, 'No fue posible aprobar la solicitud.'));
    } finally {
      setIsSaving(false);
    }
  };

  return <Modal
    title={isModify ? 'Aprobar solicitud de cambio' : 'Aprobar anulación'}
    isBusy={isSaving}
    onClose={onClose}
    footer={<>
      <button className="button button--ghost" disabled={isSaving} onClick={onClose} type="button">Cancelar</button>
      <button className={`button ${isModify && !isModifyShiftClose ? 'button--primary' : 'button--danger'}`} disabled={isSaving} onClick={() => void confirmApprove()} type="button">{isSaving ? 'Aprobando...' : isModifyShiftClose ? 'Aprobar y anular corte' : isModify ? 'Marcar como resuelta' : 'Aprobar anulación'}</button>
    </>}
  >
    {error && <div className="alert alert--error">{error}</div>}
    <dl className="detail-list">
      <div><dt>Tienda</dt><dd>{request.storeName}</dd></div>
      <div><dt>Registro</dt><dd>{VOID_REQUEST_TARGET_TYPE_LABELS[request.targetType]} · {request.targetLabel || request.targetId}</dd></div>
      <div><dt>Solicitante</dt><dd>{request.requestedByName || 'Sin usuario'}</dd></div>
      {isModify
        ? <div><dt>Cambio solicitado</dt><dd>{request.requestedChange || request.reason}</dd></div>
        : <div><dt>Motivo</dt><dd>{request.reason}</dd></div>}
    </dl>
    {isModifyShiftClose
      ? <div className="alert alert--warning">Un corte cerrado no se edita: al aprobar, <strong>el corte se ANULARÁ automáticamente</strong> (el motivo de anulación llevará el cambio pedido) y solo quedará capturarlo de nuevo con los datos correctos. Acción auditada.</div>
      : isModify
        // P-075 (S15): antes decía «Aplica tú el cambio en el registro antes de aprobar», pero el registro
        // está CONFIRMADO y un confirmado no se edita: la instrucción era imposible. Aprobar sigue haciendo
        // lo mismo (marca la solicitud como resuelta); el texto ahora dice el único camino real.
        ? <div className="alert alert--warning"><strong>{CONFIRMED_RECORD_NOUN[request.targetType] ?? 'Un registro confirmado'} no se puede editar.</strong> Para corregirlo, anúlalo desde su detalle («Ver registro») y que se capture de nuevo con los datos correctos. Aprobar solo marca esta solicitud como resuelta: no cambia nada del registro.</div>
        : <div className="alert alert--warning">Esto aplicará la anulación real del registro. Esta acción quedará auditada.</div>}
  </Modal>;
}

/** Cómo se nombra el registro confirmado de cada tipo en el aviso de un cambio (con su género). */
const CONFIRMED_RECORD_NOUN: Partial<Record<VoidRequestTargetType, string>> = {
  PURCHASE: 'Una compra confirmada',
  OTHER_EXPENSE: 'Un gasto confirmado',
  OTHER_SALE: 'Una venta confirmada',
};

function RejectVoidRequestModal({ request, onClose, onRejected }: { request: VoidRequestItem; onClose: () => void; onRejected: (message: string) => Promise<void> }) {
  const [reviewNotes, setReviewNotes] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string>();
  const isValid = reviewNotes.trim().length >= 10;
  const isModify = request.type === 'MODIFY';

  const confirmReject = async () => {
    if (isSaving || !isValid) return;
    setIsSaving(true);
    setError(undefined);
    try {
      const result = await rejectVoidRequest(request.id, { reviewNotes: reviewNotes.trim() });
      await onRejected(result.message);
    } catch (rejectError) {
      setError(getErrorMessage(rejectError, 'No fue posible rechazar la solicitud.'));
    } finally {
      setIsSaving(false);
    }
  };

  // P-075 (C2): el título decía «de anulación» también al rechazar un cambio.
  return <Modal
    title={isModify ? 'Rechazar solicitud de cambio' : 'Rechazar solicitud de anulación'}
    isBusy={isSaving}
    onClose={onClose}
    footer={<>
      <button className="button button--ghost" disabled={isSaving} onClick={onClose} type="button">Cancelar</button>
      <button className="button button--danger" disabled={isSaving || !isValid} onClick={() => void confirmReject()} type="button">{isSaving ? 'Rechazando...' : 'Rechazar'}</button>
    </>}
  >
    {error && <div className="alert alert--error">{error}</div>}
    <dl className="detail-list">
      <div><dt>Tienda</dt><dd>{request.storeName}</dd></div>
      <div><dt>Tipo</dt><dd>{VOID_REQUEST_TARGET_TYPE_LABELS[request.targetType]}</dd></div>
      <div><dt>Registro</dt><dd>{request.targetLabel || request.targetId}</dd></div>
      {isModify
        ? <div><dt>Cambio pedido</dt><dd>{request.requestedChange || request.reason}</dd></div>
        : <div><dt>Motivo de la solicitud</dt><dd>{request.reason}</dd></div>}
    </dl>
    <label className="field">
      <span>Motivo del rechazo (mínimo 10 caracteres)</span>
      <textarea onChange={(event) => setReviewNotes(event.target.value)} rows={3} value={reviewNotes} />
    </label>
  </Modal>;
}
